import { DownloadSharp, SearchSharp } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Card,
  CircularProgress,
  createFilterOptions,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CustomChip from "../../components/CustomChip";
import CustomDatePicker from "../../components/CustomDatePicker";
import CustomTable from "../../components/CustomTable";
import TaskCsvExportDialog from "../../components/TaskCsvExportDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import useGlobal from "../../hooks/useGlobal";
import useTag from "../../hooks/useTag";
import useTask from "../../hooks/useTask";
import { getUsersSearch } from "../../services/user.service";
import { SelectOptions } from "../../types/global";
import { Tag } from "../../types/tag";
import { Task } from "../../types/task";
import {
  currentStatusList,
  getOrderTypeLabel,
  getTaskTypeLabel,
  searchTypeList,
} from "../../utils/constants";
import { parseDateFromDb } from "../../utils/date.config";

const filterUserSearch = createFilterOptions<SelectOptions>();

const headers = [
  {
    title: "Task No.",
    className: "text-sm font-semibold",
  },
  {
    title: "ECP Code",
    className: "text-sm font-semibold min-w-[220px]",
  },
  {
    title: "ECP Tag",
    className: "text-center text-sm font-semibold min-w-[182px]",
  },
  {
    title: "Tag Add",
    className: "text-center text-sm font-semibold text-nowrap min-w-[140px]",
  },
  {
    title: "Order QTY",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Task Type",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Order Type",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Order Cycle",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Status",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Rejected Count",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Tracking NO.",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Create Time",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Create By",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Key-In Time",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Key-In By",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Last Support Reason",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Last Support Time",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "Last Support By",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  // {
  //   title: "QC Reason",
  //   className: "text-center text-sm font-semibold text-nowrap",
  // },
  {
    title: "QC Time",
    className: "text-center text-sm font-semibold text-nowrap",
  },
  {
    title: "QC By",
    className: "text-center text-sm font-semibold text-nowrap",
  },
];

export default function TaskList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { getOrderCycleBadge } = useGlobal();
  const { renderAllECPTags } = useTag();
  const { getStageStatus, getRejectedCount, getTaskNo } = useTask();

  useEffect(() => {
    setBreadcrumbs([{ label: "Tasks", to: "/user/tasks" }]);
  }, [setBreadcrumbs]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState<any>({});
  const [searchType, setSearchType] = useState("ecp_code");
  const [searchValue, setSearchValue] = useState("");
  const [userOptions, setUserOptions] = useState<SelectOptions[]>([]);
  const [userOptionsLoading, setUserOptionsLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");

  useEffect(() => {
    return () => {
      setPage(0);
      setPageSize(10);
      setFilter({});
      setSearchType("ecp_code");
      setSearchValue("");
      setSearchStartDate("");
      setSearchEndDate("");
    };
  }, []);

  const params = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return Object.fromEntries(queryParams.entries());
  }, [location.search]);

  useEffect(() => {
    if (
      (params?.type && params?.value) ||
      (params?.type && params?.start && params?.end)
    ) {
      setSearchType(params.type);

      if (params.type === "created_date") {
        setSearchStartDate(params.start);
        setSearchEndDate(params.end);
        setFilter({
          create_at_start: params.start,
          create_at_end: params.end,
        });
      } else {
        setSearchValue(params.value);
        setFilter({
          [params.type]: params.value,
        });
      }
    } else {
      setSearchType("ecp_code");
      setSearchValue("");
      setFilter({});
    }
    setPage(0);
  }, [params]);

  const currentPage = useMemo(() => {
    return page + 1;
  }, [page]);

  const { data, dataLoading } = useTask(true, null, {
    page_number: currentPage,
    page_size: pageSize,
    sort: "created_at|desc",
    ...filter,
  });

  const columns = [
    {
      key: "number",
      value: (data: Task) => getTaskNo(data),
    },
    {
      key: "ecp_code",
      value: (data: Task) => (
        <>
          <div>{data?.ecp?.code}</div>
          <div className="text-sm text-gray-400">{data?.ecp?.name}</div>
        </>
      ),
    },
    {
      key: "ecp_tag",
      value: (data: Task) => <>{data?.ecp && renderAllECPTags(data?.ecp)}</>,
      className: "text-center",
    },
    {
      key: "tags_response",
      value: (data: Task) => {
        const tags = data?.tags_response;
        return (
          <div className="flex flex-wrap gap-1 justify-center">
            {tags?.map((tag: Tag, index: number) => (
              <CustomChip
                key={`tag-${index}`}
                label={tag?.name || ""}
                textColor={tag.text_color_code}
                bgColor={tag.background_color_code}
              />
            ))}
          </div>
        );
      },
      className: "text-center",
    },
    {
      key: "order_quantity",
      value: (data: Task) => data?.order_quantity,
      className: "text-center",
    },
    {
      key: "task_type",
      value: (data: Task) => getTaskTypeLabel(data?.task_type),
      className: "text-center text-nowrap",
    },
    {
      key: "order_type",
      value: (data: Task) => getOrderTypeLabel(data?.order_type),
      className: "text-center text-nowrap",
    },
    {
      key: "order_cycle",
      value: (data: Task) => getOrderCycleBadge(data?.order_cycle),
      className: "text-center",
    },
    {
      key: "current_status",
      value: (data: Task) => getStageStatus(data?.current_status),
      className: "text-center",
    },
    {
      key: "rejected_count",
      value: (data: Task) => getRejectedCount(data),
      className: "text-center",
    },
    {
      key: "tracking_numbers",
      value: (data: Task) => data?.tracking_numbers?.join(", "),
    },
    {
      key: "create_task_at",
      value: (data: Task) =>
        data?.create_task_at && parseDateFromDb(data?.create_task_at),
      className: "text-center text-nowrap",
    },
    {
      key: "create_task_name",
      value: (data: Task) => data?.create_task_name,
      className: "text-center text-nowrap",
    },
    {
      key: "key_in_at",
      value: (data: Task) =>
        data?.key_in_at && parseDateFromDb(data?.key_in_at),
      className: "text-center text-nowrap",
    },
    {
      key: "key_in_name",
      value: (data: Task) => data?.key_in_name,
      className: "text-center text-nowrap",
    },
    {
      key: "last_support_reason",
      value: (data: Task) => data?.issue?.value,
      className: "text-center",
    },
    {
      key: "last_support_at",
      value: (data: Task) => {
        return data?.support_at && parseDateFromDb(data?.support_at);
      },
      className: "text-center text-nowrap",
    },
    {
      key: "last_support_name",
      value: (data: Task) => {
        return data?.support_name;
      },
      className: "text-center text-nowrap",
    },
    // {
    //   key: "qc_reason",
    //   value: (data: Task) => "",
    // },
    {
      key: "qc_at",
      value: (data: Task) => data?.qc_at && parseDateFromDb(data?.qc_at),
      className: "text-center text-nowrap",
    },
    {
      key: "qc_name",
      value: (data: Task) => data?.qc_name,
      className: "text-center text-nowrap",
    },
  ];

  const onPageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSearch = () => {
    if (
      searchValue ||
      (searchStartDate !== "Invalid Date" && searchEndDate !== "Invalid Date")
    ) {
      if (searchType === "created_date") {
        navigate(
          `/user/tasks?type=created_date&start=${searchStartDate}&end=${searchEndDate}`,
        );
      } else {
        navigate(`/user/tasks?type=${searchType}&value=${searchValue}`);
      }
    } else {
      navigate(`/user/tasks`);
    }
  };

  const onView = (data: Task) => {
    navigate(`/user/tasks/${data?.id}`);
  };

  const debouncedSearch = useRef(
    _.debounce(async (search: string) => {
      await fetchUsersSearch({ name: search });
    }, 500),
  );

  const fetchUsersSearch = async (search: Record<string, any> = {}) => {
    setUserOptionsLoading(true);
    try {
      let items: SelectOptions[] = [];
      const response = await getUsersSearch(search);
      if (response && response?.length > 0) {
        items = response.map((res: { name: string; id: string }) => ({
          label: res.name,
          value: res.id,
        }));
      }
      setUserOptions(items);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setUserOptionsLoading(false);
    }
  };

  // @ts-ignore
  const handleOpenUserSearch = async () => {
    await fetchUsersSearch();
  };

  const handleCloseUserSearch = () => {
    setUserOptions([]);
  };

  const onUserSearch = (
    _event: React.ChangeEvent<{}>,
    newInputValue: string,
    reason: string,
  ) => {
    if (reason === "input") {
      debouncedSearch.current(newInputValue);
    }
  };

  const showTextFieldSearch = useMemo(() => {
    const textFieldSearch = ["ecp_code", "number", "tracking_number"];
    return textFieldSearch.includes(searchType);
  }, [searchType]);

  const showAutocompleteSearch = useMemo(() => {
    const autocompleteSearch = ["create_task_by", "key_in_by"];
    return autocompleteSearch.includes(searchType);
  }, [searchType]);

  const onExport = () => {
    setExportDialogOpen(true);
  };

  return (
    <>
      <Typography variant="subtitle1" className="font-bold mb-4">
        Task List
      </Typography>
      <Card className="p-0 mb-4">
        <div className="w-full p-4 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex flex-col md:flex-row gap-2 items-center">
            <Select
              displayEmpty
              size="small"
              onChange={(e) => {
                setSearchValue("");
                setSearchType(e.target.value);
              }}
              fullWidth
              value={searchType}
              renderValue={(selected) => {
                if (selected === "") {
                  return <div className="text-text-disabled">Search Type</div>;
                }

                const selectedType = searchTypeList.find(
                  (st) => st.value === selected,
                );

                if (selectedType) {
                  return selectedType.label;
                }

                return null;
              }}
            >
              {searchTypeList?.map((st, index) => (
                <MenuItem value={st.value} key={`search-type-${index}`}>
                  {st.label}
                </MenuItem>
              ))}
            </Select>
            {showTextFieldSearch && (
              <TextField
                fullWidth
                value={searchValue}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="start">
                        <SearchSharp />
                      </InputAdornment>
                    ),
                  },
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchValue((e.target as HTMLInputElement).value);
                }}
                size="small"
              />
            )}
            {searchType === "created_date" && (
              <>
                <CustomDatePicker
                  fullWidth
                  size="small"
                  value={searchStartDate ? dayjs(searchStartDate) : null}
                  onChange={(e: Dayjs) => {
                    setSearchStartDate(dayjs(e).format("YYYY-MM-DD"));
                    if (!searchEndDate) {
                      setSearchEndDate(dayjs(e).format("YYYY-MM-DD"));
                    }
                  }}
                />
                <CustomDatePicker
                  fullWidth
                  size="small"
                  value={searchEndDate ? dayjs(searchEndDate) : null}
                  onChange={(e: Dayjs) => {
                    setSearchEndDate(dayjs(e).format("YYYY-MM-DD"));
                  }}
                />
              </>
            )}
            {searchType === "current_status" && (
              <Select
                displayEmpty
                size="small"
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                fullWidth
                value={searchValue}
                renderValue={(selected) => {
                  if (selected === "") {
                    return (
                      <div className="text-text-disabled">Search Status</div>
                    );
                  }

                  const selectedType = currentStatusList.find(
                    (cs) => cs.value === selected,
                  );

                  if (selectedType) {
                    return selectedType.label;
                  }

                  return null;
                }}
              >
                {currentStatusList?.map((cs, index) => (
                  <MenuItem value={cs.value} key={`search-type-${index}`}>
                    {cs.label}
                  </MenuItem>
                ))}
              </Select>
            )}
            {showAutocompleteSearch && (
              <Autocomplete
                filterOptions={(options, params) => {
                  const filtered = filterUserSearch(options, params);

                  return filtered;
                }}
                // onOpen={handleOpenUserSearch}
                onClose={handleCloseUserSearch}
                options={userOptions}
                loading={userOptionsLoading}
                onInputChange={onUserSearch}
                // value={user}
                onChange={(_event, newValue) => {
                  setSearchValue(newValue?.value || "");
                }}
                getOptionLabel={(option) => option.label}
                size="small"
                sx={{ minWidth: "220px", width: "100%" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Users"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {userOptionsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      },
                    }}
                  />
                )}
              />
            )}
            <Button
              type="button"
              variant="contained"
              className="w-full md:max-w-fit"
              startIcon={<SearchSharp className="text-white" />}
              onClick={onSearch}
            >
              Search
            </Button>
          </div>
          <Button
            type="button"
            variant="outlined"
            className="w-full md:max-w-fit"
            startIcon={<DownloadSharp />}
            onClick={onExport}
          >
            Export
          </Button>
        </div>
        <CustomTable
          showRowNo={true}
          loading={dataLoading}
          headers={headers}
          rows={data?.tasks ?? []}
          columns={columns}
          totalRecords={data?.total_record ?? 1}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onView={onView}
          actionPosition="start"
        />
      </Card>
      <TaskCsvExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </>
  );
}
